import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { getArticleBySlug, getRecentArticles } from "@/mocks/articles";

const ARTICLE_IMAGES: Record<string, string> = {
  "guide-industrial-connector-selection": "https://readdy.ai/api/search-image?query=Professional%20engineer%20selecting%20industrial%20connectors%20from%20a%20display%20board%2C%20clean%20workshop%20background%2C%20warm%20neutral%20lighting%2C%20focused%20composition%2C%20technical%20B2B%20photography%20style&width=1200&height=600&seq=art-detail-guide-industrial&orientation=landscape",
  "waterproof-connector-application": "https://readdy.ai/api/search-image?query=Outdoor%20LED%20landscape%20lighting%20installation%20with%20waterproof%20connectors%2C%20garden%20at%20dusk%2C%20warm%20golden%20hour%20lighting%2C%20professional%20outdoor%20engineering%20photography%2C%20detailed%20connection%20points%20visible&width=1200&height=600&seq=art-detail-waterproof-app&orientation=landscape",
  "smart-home-connector-trends": "https://readdy.ai/api/search-image?query=Modern%20smart%20home%20living%20room%20with%20smart%20plugs%20and%20connected%20devices%2C%20clean%20interior%20design%2C%20warm%20ambient%20lighting%2C%20lifestyle%20technology%20photography%2C%20cozy%20modern%20atmosphere&width=1200&height=600&seq=art-detail-smart-home&orientation=landscape",
  "connector-maintenance-guide": "https://readdy.ai/api/search-image?query=Technician%20cleaning%20and%20inspecting%20electrical%20connectors%20at%20workbench%2C%20tools%20and%20cleaning%20supplies%20visible%2C%20workshop%20environment%2C%20bright%20task%20lighting%2C%20professional%20maintenance%20photography&width=1200&height=600&seq=art-detail-maintenance&orientation=landscape",
  "custom-connector-development": "https://readdy.ai/api/search-image?query=Engineering%20team%20discussing%20custom%20connector%20design%20with%203D%20CAD%20models%20on%20large%20display%20screen%2C%20modern%20R&width=1200&height=600&seq=art-detail-custom-dev&orientation=landscape",
  "construction-site-safety": "https://readdy.ai/api/search-image?query=Construction%20site%20electrical%20safety%20setup%20with%20distribution%20box%20and%20protected%20connectors%2C%20workers%20in%20safety%20gear%2C%20bright%20outdoor%20lighting%2C%20professional%20industrial%20safety%20photography%2C%20organized%20cable%20management&width=1200&height=600&seq=art-detail-construction-safety&orientation=landscape",
  "connector-quality-standards": "https://readdy.ai/api/search-image?query=Quality%20control%20laboratory%20with%20testing%20equipment%20for%20electrical%20connectors%2C%20technicians%20in%20white%20coats%20inspecting%20products%2C%20clean%20bright%20laboratory%20lighting%2C%20professional%20industrial%20photography%2C%20precision%20instruments%20visible&width=1200&height=600&seq=art-detail-quality&orientation=landscape",
  "future-of-connector-technology": "https://readdy.ai/api/search-image?query=Futuristic%20concept%20of%20smart%20connectors%20with%20glowing%20data%20visualization%2C%20clean%20tech%20laboratory%20background%2C%20warm%20orange%20accent%20lighting%2C%20innovation%20technology%20photography%2C%20abstract%20digital%20patterns&width=1200&height=600&seq=art-detail-future-tech&orientation=landscape",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const article = getArticleBySlug(slug || "");
  const relatedArticles = getRecentArticles(3).filter(
    (a) => a.slug !== slug
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <Link
              to="/articles"
              className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentHtml = article.content
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-foreground-800 mt-6 mb-3">$1</h3>')
    .replace(/^- (.+)$/gm, '<li class="text-sm text-foreground-700 leading-relaxed ml-4 mb-1">$1</li>')
    .replace(/(\d+)\. (.+)/g, '<li class="text-sm text-foreground-700 leading-relaxed ml-4 mb-1">$1. $2</li>')
    .replace(/\n\n/g, '</p><p class="text-sm text-foreground-700 leading-relaxed mb-4">')
    .replace(/^\*\*(.+)\*\*$/gm, '<strong class="font-semibold">$1</strong>')
    .replace(/\|(.+)\|/g, (match: string) => {
      const cells = match.split('|').filter(Boolean).map((c: string) => c.trim());
      const isHeader = cells.some((c: string) => c.includes('---'));
      if (isHeader) return '';
      const cellTag = 'td';
      return `<tr class="border-b border-background-200">${cells.map((c: string) => `<${cellTag} class="px-3 py-2 text-xs text-foreground-700">${c}</${cellTag}>`).join('')}</tr>`;
    });

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t("articles.page.title"), href: "/articles" },
                { label: article.title },
              ]}
            />

            <div className="mt-6">
              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </div>
        </article>

        <section className="py-8 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={ARTICLE_IMAGES[article.id]}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{
                __html: `<p class="text-sm text-foreground-700 leading-relaxed mb-4">${contentHtml}</p>`,
              }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              {article.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-2">
                        <span>{ra.date}</span>
                        <span>{ra.readTime}</span>
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Article Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              author: {
                "@type": "Person",
                name: article.author,
              },
              datePublished: article.date,
              publisher: {
                "@type": "Organization",
                name: "久久插",
                url: "https://www.jiujiucha.com/",
              },
              breadcrumb: {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: "https://www.jiujiucha.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "技术资讯",
                    item: "https://www.jiujiucha.com/articles",
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: article.title,
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}